import React, { useId } from 'react';

/**
 * CrimsonScan lens logo — Terminator eye aesthetic.
 * Mechanical iris with radial spokes, segmented rings, glowing pupil,
 * and a red scan sweep.
 *
 * @param {number} size - SVG width/height in px
 * @param {boolean} animated - Enable enhanced animations for loading/splash contexts
 */
export default function CrimsonLogo({ size = 48, animated = false }) {
  const spokes = Array.from({ length: 12 }, (_, i) => i * 30);
  // Use instance-unique IDs so multiple logos on the same page don't conflict
  const uid = useId();
  const id = (name) => `cs-${name}${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        {/* Deep iris body */}
        <radialGradient id={id('iris')} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#1a0000" />
          <stop offset="60%"  stopColor="#0f0000" />
          <stop offset="100%" stopColor="#050000" />
        </radialGradient>

        {/* Central glowing pupil */}
        <radialGradient id={id('pupil')} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#ff6622" />
          <stop offset="30%"  stopColor="#ee2200" />
          <stop offset="70%"  stopColor="#990000" />
          <stop offset="100%" stopColor="#1a0000" />
        </radialGradient>

        {/* Scan sweep — red-tinted, like infrared targeting */}
        <linearGradient id={id('sweep')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#ff3300" stopOpacity="0" />
          <stop offset="30%"  stopColor="#ff3300" stopOpacity={animated ? '0.28' : '0.18'} />
          <stop offset="50%"  stopColor="#ff6622" stopOpacity={animated ? '0.45' : '0.32'} />
          <stop offset="70%"  stopColor="#ff3300" stopOpacity={animated ? '0.28' : '0.18'} />
          <stop offset="100%" stopColor="#ff3300" stopOpacity="0" />
        </linearGradient>

        {/* Clip to lens boundary */}
        <clipPath id={id('clip')}>
          <circle cx="60" cy="60" r="50" />
        </clipPath>

        {/* Outer red glow */}
        <filter id={id('glow')} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
          <feColorMatrix in="blur" type="matrix"
            values="1 0 0 0 0.6
                    0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0.7 0"
            result="glow"
          />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Pupil inner glow */}
        <filter id={id('pupil-glow')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation={animated ? '5' : '3'} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Animated outer glow ring (loading mode only) */}
        {animated && (
          <filter id={id('ring-glow')} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* ── Animated outer glow ring (loading mode) ── */}
      {animated && (
        <circle cx="60" cy="60" r="57" fill="none" stroke="#ff3300" strokeWidth="1.5" filter={`url(#${id('ring-glow')})`}>
          <animate attributeName="opacity" values="0.15;0.45;0.15" dur="3s" repeatCount="indefinite" />
        </circle>
      )}

      {/* ── Outer housing ── */}
      <circle cx="60" cy="60" r="58" fill="#080000" />

      {/* Segmented bezel — tick marks (rotates in animated mode) */}
      <circle
        cx="60" cy="60" r="54.5"
        fill="none"
        stroke="#3a0000"
        strokeWidth="3.5"
        strokeDasharray="5 4.2"
      >
        {animated && (
          <animateTransform attributeName="transform" type="rotate" from="0 60 60" to="360 60 60" dur="30s" repeatCount="indefinite" />
        )}
      </circle>

      {/* Inner bezel ring */}
      <circle cx="60" cy="60" r="51.5" fill="none" stroke="#1a0000" strokeWidth="1" />

      {/* ── Iris body ── */}
      <circle cx="60" cy="60" r="50" fill={`url(#${id('iris')})`} />

      {/* ── Crosshair lines (clipped) ── */}
      <g clipPath={`url(#${id('clip')})`} stroke="#2a0000" strokeWidth="0.7" opacity="0.7">
        <line x1="10" y1="60" x2="110" y2="60" />
        <line x1="60" y1="10" x2="60"  y2="110" />
      </g>

      {/* ── Radial spokes (counter-rotate in animated mode) ── */}
      <g stroke="#3a0000" strokeWidth="0.8" opacity="0.85">
        {animated && (
          <animateTransform attributeName="transform" type="rotate" from="360 60 60" to="0 60 60" dur="45s" repeatCount="indefinite" />
        )}
        {spokes.map(deg => (
          <line
            key={deg}
            x1="60" y1="17"
            x2="60" y2="38"
            transform={`rotate(${deg} 60 60)`}
          />
        ))}
      </g>

      {/* ── Concentric iris rings ── */}
      {/* Outer segmented ring (rotates in animated mode) */}
      <circle
        cx="60" cy="60" r="44"
        fill="none"
        stroke="#4a0000"
        strokeWidth="1"
        strokeDasharray="7 5"
      >
        {animated && (
          <animateTransform attributeName="transform" type="rotate" from="0 60 60" to="-360 60 60" dur="20s" repeatCount="indefinite" />
        )}
      </circle>
      {/* Mid ring — solid */}
      <circle cx="60" cy="60" r="36" fill="none" stroke="#3d0000" strokeWidth="0.8" />
      {/* Inner ring — segmented */}
      <circle
        cx="60" cy="60" r="26"
        fill="none"
        stroke="#550000"
        strokeWidth="1"
        strokeDasharray="4 3.5"
      >
        {animated && (
          <animateTransform attributeName="transform" type="rotate" from="0 60 60" to="360 60 60" dur="12s" repeatCount="indefinite" />
        )}
      </circle>

      {/* ── Cardinal notches on outer ring ── */}
      <g fill="#550000">
        <rect x="58.5" y="12" width="3" height="6" rx="0.5" />
        <rect x="58.5" y="102" width="3" height="6" rx="0.5" />
        <rect x="12" y="58.5" width="6" height="3" rx="0.5" />
        <rect x="102" y="58.5" width="6" height="3" rx="0.5" />
      </g>

      {/* ── Glowing pupil ── */}
      <circle
        cx="60" cy="60" r="28"
        fill={`url(#${id('pupil')})`}
        filter={`url(#${id('pupil-glow')})`}
      >
        <animate
          attributeName="opacity"
          values={animated ? '0.7;1;0.7' : '0.85;1;0.85'}
          dur={animated ? '1.8s' : '2.2s'}
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
        />
      </circle>

      {/* Pupil center bright core */}
      <circle cx="60" cy="60" r="11" fill="#ff4400" opacity="0.9" />
      <circle cx="60" cy="60" r="6"  fill="#ff8844" opacity="0.85" />
      <circle cx="60" cy="60" r="2"  fill="white"   opacity="0.6" />

      {/* Tiny specular on glass surface (upper-left) */}
      <ellipse
        cx="41" cy="39" rx="6" ry="3.5"
        fill="white" opacity="0.1"
        transform="rotate(-30 41 39)"
      />

      {/* ── Scan sweep ── */}
      <rect
        x="-80" y="0" width={animated ? '90' : '75'} height="120"
        fill={`url(#${id('sweep')})`}
        clipPath={`url(#${id('clip')})`}
      >
        <animate
          attributeName="x"
          values="-80;155;155"
          keyTimes="0;0.38;1"
          dur={animated ? '3s' : '4s'}
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.4 0 0.2 1;0 0 1 1"
        />
      </rect>
    </svg>
  );
}
